import React, { useState, useEffect } from 'react';
import { timeEntriesAPI } from '../services/api';
import { useApi } from '../hooks/useApi';
import { TimeEntry } from '../types';
import { 
  Plus, 
  Search, 
  Clock, 
  Play,
  Square,
  Edit, 
  Trash2,
  DollarSign,
  AlertCircle
} from 'lucide-react';

const TimeEntries: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [currentProject, setCurrentProject] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  const [timerStartTime, setTimerStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  
  const { data: timeEntries, loading, error, refetch } = useApi<TimeEntry[]>(
    () => timeEntriesAPI.getAll(),
    []
  );

  const [newEntry, setNewEntry] = useState({
    project_name: '',
    description: '',
    start_time: '',
    end_time: '',
    duration: '',
    hourly_rate: '',
    is_billable: true
  });

  // Calculate duration automatically when start_time and end_time change
  useEffect(() => {
    if (newEntry.start_time && newEntry.end_time) {
      const startTime = new Date(newEntry.start_time);
      const endTime = new Date(newEntry.end_time);
      
      if (endTime > startTime) {
        const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));
        setNewEntry(prev => ({ ...prev, duration: durationMinutes.toString() }));
      }
    }
  }, [newEntry.start_time, newEntry.end_time]);

  // Timer effect
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isTimerRunning && timerStartTime) {
      interval = setInterval(() => {
        setElapsedTime(Date.now() - timerStartTime.getTime());
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timerStartTime]);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDurationFromMinutes = (totalMinutes: string | number) => {
    const minutes = typeof totalMinutes === 'string' ? parseInt(totalMinutes) || 0 : totalMinutes;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatElapsedTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const filteredTimeEntries = timeEntries?.filter(entry => 
    entry.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const totalHours = filteredTimeEntries.reduce((total, entry) => total + (entry.duration || 0), 0);
  const billableHours = filteredTimeEntries.filter(entry => entry.is_billable).reduce((total, entry) => total + (entry.duration || 0), 0);

  const startTimer = () => {
    if (!currentProject.trim()) {
      alert('Please enter a project name');
      return;
    }
    
    setIsTimerRunning(true);
    setTimerStartTime(new Date());
    setElapsedTime(0);
  };

  const stopTimer = async () => {
    if (!timerStartTime) return;
    
    const endTime = new Date();
    const durationMinutes = Math.floor((endTime.getTime() - timerStartTime.getTime()) / (1000 * 60));
    
    try {
      await timeEntriesAPI.create({
        project_name: currentProject,
        description: currentDescription,
        start_time: timerStartTime.toISOString(),
        end_time: endTime.toISOString(),
        duration: durationMinutes,
        is_billable: true
      });
      
      setIsTimerRunning(false);
      setTimerStartTime(null);
      setElapsedTime(0);
      setCurrentProject('');
      setCurrentDescription('');
      refetch();
    } catch (error) {
      console.error('Error saving time entry:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const entryData = {
        ...newEntry,
        duration: parseInt(newEntry.duration),
        hourly_rate: newEntry.hourly_rate ? parseFloat(newEntry.hourly_rate) : undefined
      };

      if (editingEntry) {
        await timeEntriesAPI.update(editingEntry.id, entryData);
      } else {
        await timeEntriesAPI.create(entryData);
      }
      
      setShowAddModal(false);
      setEditingEntry(null);
      resetForm();
      refetch();
    } catch (error) {
      console.error('Error saving time entry:', error);
    }
  };

  const resetForm = () => {
    setNewEntry({
      project_name: '',
      description: '',
      start_time: '',
      end_time: '',
      duration: '',
      hourly_rate: '',
      is_billable: true
    });
  };

  const handleEdit = (entry: TimeEntry) => {
    setEditingEntry(entry);
    
    // Format datetime strings for datetime-local input
    const formatDateTimeForInput = (dateTimeString: string) => {
      if (!dateTimeString) return '';
      const date = new Date(dateTimeString);
      return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm format
    };

    setNewEntry({
      project_name: entry.project_name,
      description: entry.description || '',
      start_time: formatDateTimeForInput(entry.start_time),
      end_time: entry.end_time ? formatDateTimeForInput(entry.end_time) : '',
      duration: entry.duration?.toString() || '',
      hourly_rate: entry.hourly_rate?.toString() || '',
      is_billable: entry.is_billable
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this time entry?')) {
      try {
        await timeEntriesAPI.delete(id);
        refetch();
      } catch (error) {
        console.error('Error deleting time entry:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading time entries</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6 space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Time Entries</h1>
          <p className="mt-2 text-sm text-gray-700">
            Track your time and manage billable hours.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </button>
        </div>
      </div>

      {/* Time Tracker */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Timer</h3>
          
          {isTimerRunning && (
            <div className="mb-4 p-4 bg-primary-50 rounded-lg">
              <div className="text-center">
                <div className="text-3xl font-mono font-bold text-primary-600">
                  {formatElapsedTime(elapsedTime)}
                </div>
                <div className="text-sm text-primary-600 mt-1">
                  Working on: {currentProject}
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label htmlFor="project" className="block text-sm font-medium text-gray-700">
                Project
              </label>
              <input
                type="text"
                id="project"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Project name"
                value={currentProject}
                onChange={(e) => setCurrentProject(e.target.value)}
                disabled={isTimerRunning}
              />
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <input
                type="text"
                id="description"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="What are you working on?"
                value={currentDescription}
                onChange={(e) => setCurrentDescription(e.target.value)}
                disabled={isTimerRunning}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={isTimerRunning ? stopTimer : startTimer}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  isTimerRunning 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isTimerRunning ? (
                  <>
                    <Square className="h-4 w-4 mr-2" />
                    Stop
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Start
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Hours
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatDuration(totalHours)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Billable Hours
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatDuration(billableHours)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-primary-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Non-Billable Hours
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatDuration(totalHours - billableHours)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Search time entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Time Entries Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {filteredTimeEntries.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No time entries</h3>
              <p className="mt-1 text-sm text-gray-500">
                Start tracking your time to see entries here.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Entry
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Billable
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTimeEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {entry.project_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {entry.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {entry.duration ? formatDuration(entry.duration) : 'In progress'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {entry.hourly_rate ? `£${entry.hourly_rate}/hr` : 'No rate'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          entry.is_billable 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {entry.is_billable ? 'Billable' : 'Non-billable'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(entry.start_time).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => handleEdit(entry)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDelete(entry.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Time Entry Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        {editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Project Name *
                          </label>
                          <input
                            type="text"
                            required
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            value={newEntry.project_name}
                            onChange={(e) => setNewEntry({ ...newEntry, project_name: e.target.value })}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            rows={3}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            value={newEntry.description}
                            onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Start Time *
                            </label>
                            <input
                              type="datetime-local"
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              value={newEntry.start_time}
                              onChange={(e) => setNewEntry({ ...newEntry, start_time: e.target.value })}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              End Time
                            </label>
                            <input
                              type="datetime-local"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              value={newEntry.end_time}
                              onChange={(e) => setNewEntry({ ...newEntry, end_time: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Duration
                            </label>
                            <div className="mt-1 relative">
                              {newEntry.start_time && newEntry.end_time ? (
                                <div className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-700 sm:text-sm">
                                  {newEntry.duration ? formatDurationFromMinutes(newEntry.duration) : '0m'}
                                  <span className="text-xs text-gray-500 ml-2">
                                    ({newEntry.duration || 0} minutes)
                                  </span>
                                </div>
                              ) : (
                                <input
                                  type="number"
                                  placeholder="Duration in minutes"
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                  value={newEntry.duration}
                                  onChange={(e) => setNewEntry({ ...newEntry, duration: e.target.value })}
                                />
                              )}
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              {newEntry.start_time && newEntry.end_time 
                                ? 'Automatically calculated from start and end time'
                                : 'Enter duration manually or set start/end times for automatic calculation'
                              }
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Hourly Rate
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="e.g., 50.00"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              value={newEntry.hourly_rate}
                              onChange={(e) => setNewEntry({ ...newEntry, hourly_rate: e.target.value })}
                            />
                            {newEntry.duration && newEntry.hourly_rate && (
                              <p className="mt-1 text-xs text-green-600 font-medium">
                                Total: ${((parseInt(newEntry.duration) / 60) * parseFloat(newEntry.hourly_rate)).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="is_billable"
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            checked={newEntry.is_billable}
                            onChange={(e) => setNewEntry({ ...newEntry, is_billable: e.target.checked })}
                          />
                          <label htmlFor="is_billable" className="ml-2 block text-sm text-gray-900">
                            Billable
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {editingEntry ? 'Update' : 'Add'} Entry
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingEntry(null);
                      resetForm();
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeEntries; 